import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  RotateCcw, Truck, Check, X, Printer, Search, ShieldAlert, Plus,
  Clock, User, FileText, Info,
} from "lucide-react";
import FakturPrint, { isStruk } from "../components/faktur/FakturPrint";
import StrukPrint from "../components/faktur/StrukPrint";
import PemilihTemplate from "../components/faktur/PemilihTemplate";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";
import InputRupiah from "../components/InputRupiah";
import { hitungNominalReturDefault, susunReturDariNota, bisaKelolaRetur, syaratTukarUnit, syaratTagihanRetur } from "@/lib/retur";
import GerbangLengkapiNoHp from "../components/retur/GerbangLengkapiNoHp";
import { hpSah, periksaKunciHp, pesanHpTidakSah } from "../lib/noHp";

/** Retur Penjualan & Retur Pembelian (brief bos).
 *
 * KONTRAK BACKEND (routers/retur.py — dibaca, tidak disunting):
 *  - POST/GET/detail/approve/tolak untuk kedua jenis mengikuti pola
 *    "pengajuan murni dokumen, approve yang benar-benar menulis jurnal/stok".
 *  - Approve/Tolak digerbangi role superadmin/admin/"*spv*" ATAU izin
 *    eksplisit `retur.update` — dicerminkan di `bisaKelola()` di bawah untuk
 *    UX (server tetap validator akhir, lihat `_bisa_approve_retur`).
 *
 * Cetak faktur retur memakai komponen BERSAMA `FakturPrint`/`StrukPrint`
 * yang kontraknya native untuk entitas sale/po (lihat header FakturPrint.jsx:
 * "data boleh berupa entitas mentah ATAU payload yang membungkusnya dalam key
 * sale/po/transfer"). Payload endpoint retur membungkus entitas dalam key
 * "retur" (bukan salah satu dari itu) DAN skema field retur (harga_jual_baris,
 * nominal_kembali, dst) berbeda dari skema sale/po (harga, total_harga) yang
 * dipahami `normalisasiItems`/`normalisasiEntitas`. Makanya di bawah ada
 * `adaptReturJualUntukCetak`/`adaptReturBeliUntukCetak` — adaptor LOKAL di
 * halaman ini (tidak mengubah FakturPrint.jsx) yang memetakan retur ke bentuk
 * yang sudah dipahami komponen itu, supaya nominal & baris barang yang
 * tercetak adalah milik RETUR ini, bukan nota/PO asal yang utuh.
 */

const VALID_TABS = ["penjualan", "pembelian"];

// Status unit yang masih boleh dikembalikan ke supplier — cermin
// `rekon_unit.STATUS_MASIH_DI_GUDANG` yang dipakai `buat_retur_pembelian` dan
// `setujui_retur_pembelian`. "rusak" IKUT sejak brief 4 Sep 2026 (gugus D):
// unit rusak masih di rak dan lapisan modalnya belum dikonsumsi, jadi nilainya
// utuh untuk dibalik. Status lain (hilang/terjual/transit) ditolak backend,
// jadi tombolnya dimatikan di sini — aturan #3 CLAUDE.md.
const STATUS_UNIT_BISA_RETUR = ["tersedia", "rusak"];
// `status: null` berarti respons backend LAMA tidak memberi tahu statusnya.
// Dalam keadaan itu layar tidak boleh menebak dan melarang: biarkan dipilih,
// backend yang menolak dengan pesan yang jelas kalau memang tidak boleh.
const bisaDiretur = (status) => status == null || STATUS_UNIT_BISA_RETUR.includes(status);

const STATUS_META = {
  menunggu_approval: { label: "Menunggu Approval", cls: "bg-amber-100 text-amber-800" },
  memproses: { label: "Memproses", cls: "bg-slate-100 text-slate-700" },
  disetujui: { label: "Disetujui", cls: "bg-emerald-100 text-emerald-800" },
  ditolak: { label: "Ditolak", cls: "bg-rose-100 text-rose-800" },
  // Alur retur pembelian sejak 26 Agu 2026: kompensasinya boleh ditentukan
  // BELAKANGAN. Statusnya sendiri yang memberi tahu apa yang masih ditunggu —
  // tanpa itu, retur yang barangnya sudah keluar tapi gantinya belum diterima
  // terlihat sama persis dengan yang sudah tuntas.
  menunggu_kompensasi: { label: "Perlu Pilih Kompensasi", cls: "bg-sky-100 text-sky-800" },
  memproses_kompensasi: { label: "Memproses", cls: "bg-slate-100 text-slate-700" },
  menunggu_konfirmasi: { label: "Menunggu Kompensasi Diterima", cls: "bg-violet-100 text-violet-800" },
  selesai: { label: "Selesai", cls: "bg-emerald-100 text-emerald-800" },
};

/** Bentuk pengembalian retur PENJUALAN, apa adanya dari dokumen.
 *  "Cash" vs "Transfer" di brief owner dibedakan oleh AKUN yang dipilih (kas vs
 *  bank), bukan oleh field tersendiri — jadi nama akunnya yang ditampilkan,
 *  bukan tebakan kata. */
function metodeReturJual(d) {
  if (!d) return "-";
  if ((d.jenis_kembali || "uang") === "tukar_unit") return "Tukar Unit (kredit pelanggan)";
  return d.metode_kembali_akun_nama || "Uang (akun tidak tercatat)";
}

const KOMPENSASI_LABEL = {
  potong_utang: "Potong Utang PO",
  uang_kembali: "Uang Kembali",
  kredit_doa: "Kredit DOA di Supplier",
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || "-", cls: "bg-slate-100 text-slate-700" };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${meta.cls}`}>{meta.label}</span>;
}

const inp = "h-10 px-3 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-600/40";
function Field({ l, children }) {
  return <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-600">{l}</span>{children}</label>;
}

function adaptReturJualUntukCetak(retur, sale) {
  return {
    code: retur.code,
    nama_pelanggan: retur.sale_nama_pelanggan || sale?.nama_pelanggan || "-",
    // Tukar unit: cetak No HP SNAPSHOT pengajuan — itulah kunci kreditnya (I1,
    // 22 Sep 2026). No HP nota bisa diedit sesudahnya; pelanggan tidak boleh
    // pulang membawa nomor yang bukan kunci kreditnya.
    no_hp: (retur.jenis_kembali === "tukar_unit" && "sale_no_hp" in retur)
      ? (retur.sale_no_hp || "") : (sale?.no_hp || ""),
    tanggal: retur.tanggal,
    waktu: sale?.waktu || "",
    branch: retur.sale_branch || sale?.branch || "",
    nama_penjual: sale?.nama_penjual || "",
    items: (retur.items || []).map((it) => ({
      nama_barang: it.nama_barang || it.sku || "-",
      sku: it.sku || "",
      imei: it.imei || "",
      qty: it.qty || 1,
      harga: Number(it.harga_jual_baris || 0),
    })),
    total_harga: retur.nominal_kembali,
    catatan: [
      retur.catatan,
      ...(retur.items || []).map((it) => (it.alasan ? `${it.nama_barang}: ${it.alasan}` : "")).filter(Boolean),
    ].filter(Boolean).join(" · "),
  };
}

function adaptReturBeliUntukCetak(retur, po) {
  const rincian = retur.rincian && retur.rincian.length ? retur.rincian : null;
  return {
    code: retur.code,
    // FakturPrint memakai supplier_nama + po_code untuk jenis retur_pembelian
    // (label "Supplier"). nama_pelanggan/no_hp tetap diisi untuk StrukPrint,
    // yang hanya mengenal skema pelanggan.
    supplier_nama: retur.supplier_nama || po?.supplier_nama || "-",
    po_code: retur.po_code || "",
    nama_pelanggan: retur.supplier_nama || po?.supplier_nama || "-",
    no_hp: retur.po_code ? `PO ${retur.po_code}` : "",
    tanggal: retur.tanggal,
    branch: retur.cabang || po?.cabang || "",
    cabang: retur.cabang || po?.cabang || "",
    warehouse_nama: retur.warehouse_nama || po?.warehouse_nama || "",
    items: rincian
      ? rincian.map((r) => ({ nama_barang: r.nama || r.sku, sku: r.sku, qty: r.qty, harga: r.qty ? r.nilai / r.qty : 0 }))
      : (retur.items || []).map((it) => ({
          nama_barang: it.sku, sku: it.sku, qty: it.qty, harga: 0, imei: (it.imeis || [])[0] || "",
        })),
    total_harga: retur.nilai_total,
    catatan: `Alasan: ${retur.alasan || "-"}${retur.kompensasi ? ` · Kompensasi: ${retur.kompensasi === "potong_utang" ? "Potong Utang" : "Uang Kembali"}` : ""}`,
  };
}

export default function Retur() {
  const { user, can } = useAuth();
  const bisaLihat = can("retur", "view");
  const bisaAjukanBeli = can("retur", "create");
  // Alur "Ajukan Retur Pembelian" mencari & membuka PO (GET /inventory/po,
  // GET /inventory/po/{id}) yang butuh izin modul terpisah inventory_po.view —
  // bukan cuma retur.create. Pola sama dengan KartuStok.jsx:35-42 (satu 403
  // tidak boleh mengunci seluruh tombol, cukup nonaktifkan dengan penjelasan).
  const bisaCariPo = can("inventory_po", "view");
  // Cermin `_bisa_approve_retur` — satu sumber di lib/retur.js (dipakai juga
  // Sales.jsx). Tombol Lengkapi No HP nota TIDAK memakai ini: gerbangnya jabatan
  // (owner 29b, SPV ke atas) — lihat `components/retur/GerbangLengkapiNoHp`.
  const bisaKelola = () => bisaKelolaRetur(user, can);
  // Daftar template cetak (menu Template Faktur & Struk) memakai izin modul
  // "faktur" — terpisah dari "retur" yang menggerbangi halaman ini. Tanpa
  // izin ini, pemilih template dilewati dan backend memilih template default
  // seperti biasa (perilaku lama, tetap jalan).
  const bolehFaktur = can("faktur", "view");

  const [searchParams, setSearchParams] = useSearchParams();
  // KENAPA baca query param: brief owner 17 Agu — "Retur" cuma nangkring di grup
  // Penjualan/POS tanpa penanda ia juga mencakup pembelian, jadi staf yang buka
  // modul Inventory tidak pernah menemukan Retur Pembelian. Perbaikannya
  // (Layout.jsx) menambahkan entri Inventory yang menaut langsung ke
  // `/retur?tab=pembelian`. Tanpa baca query string di sini, tautan itu selalu
  // diabaikan dan mendarat di tab "Retur Penjualan" (default lama) — pola sama
  // persis dengan `?filter=jatuh_tempo` di FinanceUtang.jsx yang jadi presedennya.
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(VALID_TABS.includes(urlTab) ? urlTab : "penjualan"); // "penjualan" | "pembelian"
  const [statusJual, setStatusJual] = useState("");
  const [statusBeli, setStatusBeli] = useState("");
  const [listJual, setListJual] = useState([]);
  const [listBeli, setListBeli] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [detailModal, setDetailModal] = useState(null); // {jenis, id, loading, data}
  const [kompensasiModal, setKompensasiModal] = useState(null);
  // Tagihan PO yang dipilih approver per retur (K0-3). Dipisah dari data detail
  // supaya memuat ulang detail tidak menghapus pilihan yang sedang dibuat.
  const [utangApprove, setUtangApprove] = useState({});
  // Kredit pelanggan dari retur "tukar unit" + rekonsiliasinya dengan COA 2150.
  const [kredit, setKredit] = useState(null);
  const [showKredit, setShowKredit] = useState(false);

  const muatKredit = async () => {
    try {
      const [d, r] = await Promise.all([
        api.get("/retur/kredit-pelanggan"),
        api.get("/retur/kredit-pelanggan/rekonsiliasi"),
      ]);
      setKredit({ ...d.data, rekon: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat kredit pelanggan");
    }
  };
  // Pelanggan ganti nomor (brief 22 Sep 2026, I1). Kolom bukti di form sengaja
  // TIDAK diisi otomatis: approver wajib memintanya dari pelanggan (No HP lama
  // atau nomor struk retur) — itulah yang diperiksa server. BATAS JUJUR: kedua
  // nilai itu TERLIHAT di baris daftar kredit tepat di belakang modal ini (No HP
  // & kode RTJ), jadi bukti ini kontrol PROSES + jejak (riwayat_no_hp, audit,
  // aktivitas, Telegram owner), BUKAN rahasia yang melindungi dari orang dalam.
  // {kredit, noHpBaru, bukti: "hp"|"kode", nilaiBukti, alasan, submitting}
  const [pindahModal, setPindahModal] = useState(null);
  const bukaPindah = (k) => setPindahModal({
    kredit: k, noHpBaru: "", bukti: k.no_hp_kunci ? "hp" : "kode", nilaiBukti: "", alasan: "",
    submitting: false,
  });
  const kirimPindah = async () => {
    const m = pindahModal;
    if (!m) return;
    setPindahModal(x => ({ ...x, submitting: true }));
    try {
      const { data } = await api.post(`/retur/kredit-pelanggan/${m.kredit.id}/pindah-hp`, {
        no_hp_baru: m.noHpBaru, alasan: m.alasan.trim(),
        // Kunci yang DITAMPILKAN layar ini — server menolak kalau sudah diubah
        // orang lain sejak daftar dimuat (layar basi tidak menimpa apa pun).
        no_hp_kunci_lama: m.kredit.no_hp_kunci || "",
        bukti_no_hp_lama: m.bukti === "hp" ? m.nilaiBukti : "",
        bukti_kode_retur: m.bukti === "kode" ? m.nilaiBukti : "",
      });
      toast.success(data.message || "Kredit dipindah ke No HP baru");
      setPindahModal(null);
      muatKredit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memindah kredit");
      setPindahModal(x => (x ? { ...x, submitting: false } : x));
    }
  };
  const selaraskanNama = async (k) => {
    const alasan = window.prompt(
      `Samakan nama kredit "${k.pelanggan_nama}" dengan kartu Data Pelanggan "${k.kartu_pelanggan?.nama}"?\n\n`
      + "Kartu itu ber-No HP sama dengan kredit ini. Setelah disamakan, kredit hanya bisa dipakai "
      + "dengan nama kartu + No HP ini.\n\nAlasan (wajib):", "");
    if (alasan === null) return;
    if (!alasan.trim()) { toast.error("Alasan wajib diisi"); return; }
    try {
      const { data } = await api.post(`/retur/kredit-pelanggan/${k.id}/selaraskan-nama`, {
        alasan: alasan.trim(), pelanggan_kunci_lama: k.pelanggan_kunci || "",
      });
      toast.success(data.message || "Nama kredit disamakan");
      muatKredit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyamakan nama kredit");
    }
  };
  // Langkah SADAR sesudah pembaruan aturan: isi kunci No HP kredit lama lalu
  // laporkan kredit TERTAHAN (No HP belum sah) supaya nomornya ditetapkan.
  const isiKunciKredit = async () => {
    try {
      const { data } = await api.post("/retur/kredit-pelanggan/isi-kunci-hp");
      const tertahan = (data.tertahan_aktif || []).length;
      toast.success(`Kunci No HP diperiksa: ${(data.diisi_sah || []).length} diisi, `
        + `${tertahan} kredit aktif tertahan${(data.gagal || []).length ? `, ${data.gagal.length} gagal` : ""}.`);
      muatKredit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memeriksa kunci No HP kredit");
    }
  };
  const [tolakModal, setTolakModal] = useState(null);   // {jenis, id, code, alasan, submitting}
  // {jenis, id, step, templates, templateId, loading, data} — step:
  // "memuat_template" | "pilih_template" | "kosong" | "preview".
  const [cetakModal, setCetakModal] = useState(null);
  const [kasBank, setKasBank] = useState([]);
  const [kasBankError, setKasBankError] = useState(""); // pesan bila daftar akun kas/bank gagal dimuat (mis. 403)
  const [ajukanBeli, setAjukanBeli] = useState(null);   // form ajukan retur pembelian
  const [ajukanJual, setAjukanJual] = useState(null);   // form ajukan retur penjualan (P18a, 4 Sep 2026)

  // K0-3: begitu "Potong Utang" dipilih, muat tagihan aktif PO itu. Lebih dari
  // satu tagihan (PO bertahap, satu per DO) = pengaju wajib memilih; backend
  // menolak menebak, jadi tanpa pemilih ini pengajuan selalu berakhir 400.
  const poIdAjukan = ajukanBeli?.po?.id;
  const kompAjukan = ajukanBeli?.kompensasi;
  useEffect(() => {
    if (!poIdAjukan || kompAjukan !== "potong_utang") return undefined;
    let batal = false;
    api.get("/retur/pembelian/utang-po", { params: { po_id: poIdAjukan } })
      .then(r => {
        if (batal) return;
        setAjukanBeli(m => (m && m.po?.id === poIdAjukan ? { ...m, utangAktif: r.data?.items || [] } : m));
      })
      .catch(() => {
        if (!batal) setAjukanBeli(m => (m ? { ...m, utangAktif: null } : m));
      });
    return () => { batal = true; };
  }, [poIdAjukan, kompAjukan]);

  const loadJual = async (status) => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/retur/penjualan", { params: status ? { status } : {} });
      setListJual(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar retur penjualan");
    } finally { setLoadingList(false); }
  };
  const loadBeli = async (status) => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/retur/pembelian", { params: status ? { status } : {} });
      setListBeli(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar retur pembelian");
    } finally { setLoadingList(false); }
  };
  const refreshList = (jenis) => (jenis === "penjualan" ? loadJual(statusJual) : loadBeli(statusBeli));

  // Ganti tab lewat klik: dorong balik ke URL supaya tombol Back browser dan
  // tautan yang dibagikan/di-bookmark tetap mendarat di tab yang sama (lihat
  // `selectFilter` di FinanceUtang.jsx — pola identik).
  const selectTab = (key) => {
    setTab(key);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", key);
      return next;
    }, { replace: true });
  };

  // Sinkron ulang kalau URL berubah SETELAH halaman ini sudah ter-mount (mis.
  // navigasi dari link Inventory lain sementara halaman Retur masih terbuka di
  // riwayat browser — React Router tidak selalu remount komponen yang sama).
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && VALID_TABS.includes(t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!bisaLihat) return;
    if (tab === "penjualan") loadJual(statusJual); else loadBeli(statusBeli);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bisaLihat, tab, statusJual, statusBeli]);

  const muatKasBank = async () => {
    if (kasBank.length) return kasBank;
    try {
      const { data } = await api.get("/finance/accounts/kas-bank");
      setKasBank(data || []);
      setKasBankError("");
      return data || [];
    } catch (e) {
      // Jangan gagalkan seluruh alur ajukan retur pembelian hanya karena akun
      // kas/bank tak bisa dimuat (mis. tidak punya izin finance) — kompensasi
      // "potong_utang" tidak butuh daftar ini dan harus tetap bisa dipakai.
      setKasBankError(e?.response?.data?.detail || "Daftar akun kas/bank tidak bisa dimuat (kemungkinan kurang izin).");
      return [];
    }
  };

  // ---- Detail ----
  const openDetail = async (jenis, id) => {
    setDetailModal({ jenis, id, loading: true, data: null });
    try {
      const { data } = await api.get(`/retur/${jenis}/${id}`);
      // Modal bisa sudah ditutup (atau berganti ke item lain) selagi fetch
      // berjalan — jangan buka/timpa lagi kalau state saat ini sudah bukan
      // punya request ini.
      setDetailModal(m => (m && m.jenis === jenis && m.id === id ? { jenis, id, loading: false, data } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat detail retur");
      setDetailModal(m => (m && m.jenis === jenis && m.id === id ? null : m));
    }
  };

  // ---- Approve (konfirmasi menjelaskan akibat jurnal + stok) ----
  const kirimKompensasi = async () => {
    const m = kompensasiModal;
    if (!m.kompensasi) { toast.error("Pilih bentuk kompensasinya dulu"); return; }
    if (m.kompensasi === "uang_kembali" && !m.akun_terima_id) {
      toast.error("Pilih akun penerima uang"); return;
    }
    setKompensasiModal(x => ({ ...x, submitting: true }));
    try {
      const { data } = await api.post(`/retur/pembelian/${m.id}/kompensasi`, {
        kompensasi: m.kompensasi,
        akun_terima_id: m.kompensasi === "uang_kembali" ? m.akun_terima_id : undefined,
        // Tagihan PO yang dipotong (K0-3) — wajib dipilih kalau PO-nya punya
        // lebih dari satu tagihan aktif; backend menolak menebak.
        utang_id: m.kompensasi === "potong_utang" && m.utang_id ? m.utang_id : undefined,
      });
      toast.success(data.message || "Kompensasi ditetapkan");
      setKompensasiModal(null);
      loadBeli(statusBeli);
      if (detailModal?.id === m.id) openDetail("pembelian", m.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menetapkan kompensasi");
      setKompensasiModal(x => (x ? { ...x, submitting: false } : x));
    }
  };

  const konfirmasiKompensasi = async (item) => {
    if (!window.confirm(
      `Konfirmasi kompensasi retur ${item.code} SUDAH DITERIMA?\n\n` +
      "Pakai ini hanya kalau uang/potongan/kreditnya benar-benar sudah ada. " +
      "Setelah dikonfirmasi, retur ini dianggap selesai dan kompensasinya tidak bisa diubah lagi."
    )) return;
    try {
      const { data } = await api.post(`/retur/pembelian/${item.id}/konfirmasi`);
      toast.success(data.message || "Retur selesai");
      loadBeli(statusBeli);
      setDetailModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengkonfirmasi");
    }
  };

  // Tindakan penyelesaian retur PENJUALAN (brief 4 Sep butir 18). Tidak menulis
  // jurnal apa pun — jurnalnya sudah terposting saat approve; ini pengakuan
  // bahwa uang/kreditnya benar-benar sudah sampai ke pelanggan.
  const selesaikanReturJual = async (item) => {
    const catatan = window.prompt(
      `Tandai retur ${item.code} SELESAI?

Metode: ${metodeReturJual(item)}
Pakai ini setelah uang/kreditnya benar-benar diserahkan ke pelanggan. Tidak ada jurnal baru yang ditulis, dan metodenya tidak bisa diubah lagi.

Catatan (opsional):`, "");
    if (catatan === null) return;
    try {
      const { data } = await api.post(`/retur/penjualan/${item.id}/selesai`, { catatan });
      toast.success(data.message || "Retur ditandai selesai");
      loadJual(statusJual);
      setDetailModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menandai selesai");
    }
  };

  const approve = async (jenis, item, utangId) => {
    // Tukar unit tidak mengeluarkan uang: lawan-jurnalnya 2150 dan kreditnya
    // hanya bisa dipakai pemiliknya (nama + No HP, I1) — teks lama menyebut
    // "akun kas/bank" untuk keduanya.
    const tukar = jenis === "penjualan" && (item.jenis_kembali || "uang") === "tukar_unit";
    const ident = item.kredit_identitas;
    const pesan = jenis === "penjualan"
      ? `Setujui retur ${item.code}?\n\nAkibat setelah disetujui:\n` +
        (tukar
          ? `• Jurnal retur ${idr(item.nominal_kembali)} langsung diposting (kontra pendapatan retur ↔ 2150 Titipan Kredit Retur Pelanggan) — tidak ada uang keluar.\n` +
            `• Kredit hanya bisa dipakai pelanggan atas nama ${ident?.nama || "-"} · No HP ${ident?.no_hp || "-"}.\n`
          : `• Jurnal retur ${idr(item.nominal_kembali)} langsung diposting (kontra pendapatan retur ↔ ${item.metode_kembali_akun_nama || "akun kas/bank"}).\n`) +
        `• Stok/unit yang tercatat keluar untuk transaksi asal akan dikembalikan bila datanya memungkinkan.\n\n` +
        `Tindakan ini tidak bisa dibatalkan dari halaman ini.`
      : `Setujui retur ${item.code}?\n\nAkibat setelah disetujui:\n` +
        `• Stok gudang BERKURANG sesuai barang yang dikembalikan ke supplier.\n` +
        `• ${!item.kompensasi
              ? "Nilainya PARKIR di Piutang Retur Pembelian sampai bentuk kompensasinya dipilih."
              : item.kompensasi === "potong_utang"
                ? "Jurnal diposting: memotong utang usaha PO ini."
                : `Jurnal diposting: uang keluar dari akun ${item.akun_terima_nama || "-"}.`}\n\n` +
        `Tindakan ini tidak bisa dibatalkan dari halaman ini.`;
    if (!window.confirm(pesan)) return;
    try {
      const { data } = await api.post(`/retur/${jenis}/${item.id}/approve`,
        utangId ? { utang_id: utangId } : undefined);
      toast.success(data.message || "Retur disetujui");
      refreshList(jenis);
      setDetailModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyetujui retur");
    }
  };

  // ---- Tolak ----
  const submitTolak = async () => {
    if (!tolakModal) return;
    if (!tolakModal.alasan.trim()) { toast.error("Alasan penolakan wajib diisi"); return; }
    setTolakModal(m => ({ ...m, submitting: true }));
    try {
      const { data } = await api.post(`/retur/${tolakModal.jenis}/${tolakModal.id}/tolak`, {
        alasan: tolakModal.alasan.trim(),
      });
      toast.success(data.message || "Retur ditolak");
      refreshList(tolakModal.jenis);
      setTolakModal(null);
      setDetailModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menolak retur");
      setTolakModal(m => ({ ...m, submitting: false }));
    }
  };

  // ---- Cetak Faktur Retur ---- (+ pemilih template kalau jenis "retur_penjualan"/
  // "retur_pembelian" punya lebih dari satu template)
  // `warehouseId` hanya terisi untuk retur PEMBELIAN — dokumennya mewarisi gudang
  // dari PO (routers/retur.py:630). Retur penjualan tidak punya gudang tunggal
  // (basisnya cabang), jadi paramnya sengaja tidak dikirim untuk jenis itu.
  const bukaCetak = async (jenis, id, warehouseId = null) => {
    const jenisTemplate = `retur_${jenis}`; // "retur_penjualan" | "retur_pembelian"
    setCetakModal({ jenis, id, step: "memuat_template", templates: [], templateId: null, loading: true, data: null });
    if (!bolehFaktur) { await muatDokumenCetak(jenis, id, null); return; }
    try {
      const params = { jenis: jenisTemplate };
      if (warehouseId) params.warehouse_id = warehouseId;
      const { data } = await api.get("/faktur/templates", { params });
      const daftar = data?.items || [];
      // Sama seperti openDetail: jangan buka/timpa modal cetak kalau sudah
      // ditutup atau sudah dialihkan ke dokumen lain selagi fetch berjalan.
      if (daftar.length === 0) {
        setCetakModal(m => (m && m.jenis === jenis && m.id === id ? { jenis, id, step: "kosong", templates: [], loading: false, data: null } : m));
        return;
      }
      if (daftar.length === 1) { await muatDokumenCetak(jenis, id, daftar[0]); return; }
      const def = daftar.find(t => t.is_default) || daftar[0];
      setCetakModal(m => (m && m.jenis === jenis && m.id === id
        ? { jenis, id, step: "pilih_template", templates: daftar, templateId: def.id, loading: false, data: null } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar template cetak");
      setCetakModal(m => (m && m.jenis === jenis && m.id === id ? null : m));
    }
  };

  const muatDokumenCetak = async (jenis, id, template) => {
    setCetakModal(m => ({ ...(m || { jenis, id }), jenis, id, step: "preview", loading: true, data: null }));
    try {
      const { data } = await api.get(`/faktur/data/retur-${jenis}/${id}`, {
        params: template ? { template_id: template.id } : {},
      });
      setCetakModal(m => (m && m.jenis === jenis && m.id === id ? { ...m, step: "preview", loading: false, data } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat dokumen cetak");
      setCetakModal(m => (m && m.jenis === jenis && m.id === id ? null : m));
    }
  };

  // ---- Ajukan Retur Pembelian ----
  // ---------- Ajukan Retur Penjualan (brief owner 4 Sep 2026, P18a) ----------
  // Dulu retur penjualan hanya bisa diajukan dari panel detail di halaman
  // Penjualan — staf harus tahu notanya lebih dulu. Di sini alurnya dibalik:
  // cari nota (nomor/nama/HP/IMEI/barang), pilih barang, tentukan bentuk
  // pengembalian. Rumus baris & nominal saran SAMA dengan Sales.jsx (lib/retur.js).
  // Dokumen `sales` tidak punya nomor nota — identitasnya `id`; yang
  // ditampilkan 8 karakter pertamanya, sama seperti yang dikenal staf.
  const labelNota = (s) => s?.code || (s?.id ? `#${String(s.id).slice(0, 8).toUpperCase()}` : "-");
  const openAjukanJual = () => setAjukanJual({
    // `tanggal` ikut direset tiap modal dibuka — filter hari kemarin tidak boleh
    // menempel diam-diam di pengajuan berikutnya.
    step: "cari", q: "", tanggal: "", results: [], searching: false, sale: null, rows: [],
    faktorNeto: 1, faktorPpn: 0, sisaNilaiMaks: 0,
    jenisKembali: "uang", akun: "", nominal: 0, nominalTouched: false, catatan: "", submitting: false,
  });
  /** R1 (brief owner 8 Sep 2026): pencarian teks DAN/ATAU pilih tanggal.
   *  Keduanya dikirim ke SERVER, tidak menyaring hasil di layar — daftar dari
   *  server dipotong di `limit`, jadi menyaringnya di sini akan membuat nota
   *  ke-31 tidak pernah ketemu sambil melapor "tidak ada hasil" dengan yakin
   *  (jebakan yang sudah tercatat di CLAUDE.md untuk piutang/utang). */
  const cariNota = async (q, tanggal) => {
    const teks = (q || "").trim();
    const tgl = (tanggal || "").trim();
    if (!teks && !tgl) { toast.error("Ketik kata kunci atau pilih tanggal dulu"); return; }
    if (teks && teks.length < 2) { toast.error("Ketik minimal 2 karakter"); return; }
    setAjukanJual(m => ({ ...m, searching: true }));
    try {
      const { data } = await api.get("/retur/penjualan/cari-nota", {
        params: { ...(teks ? { q: teks } : {}), ...(tgl ? { tanggal: tgl } : {}), limit: 30 } });
      setAjukanJual(m => ({ ...m, searching: false, results: data.items || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencari nota");
      setAjukanJual(m => ({ ...m, searching: false }));
    }
  };
  const pilihNota = async (sale) => {
    setAjukanJual(m => ({ ...m, searching: true }));
    try {
      const kb = await muatKasBank();
      // Sama seperti Sales.jsx: 403 pada daftar retur aktif = anggap kosong —
      // daftar itu cuma batas UX, server tetap validator akhir saat POST.
      const existing = await api.get("/retur/penjualan", { params: { sale_id: sale.id } })
        .catch(e => { if (e?.response?.status === 403) return { data: { items: [] } }; throw e; });
      const susun = susunReturDariNota(sale, existing.data?.items || []);
      setAjukanJual(m => ({
        ...m, step: "form", searching: false, sale, rows: susun.rows,
        faktorNeto: susun.faktorNeto, faktorPpn: susun.faktorPpn, sisaNilaiMaks: susun.sisaNilaiMaks,
        akun: m.akun || ((kb || []).find(a => a.category === "kas")?.id || ""),
        nominal: 0, nominalTouched: false,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat nota");
      setAjukanJual(m => ({ ...m, searching: false }));
    }
  };
  const ubahBarisJual = (i, patch) => setAjukanJual(m => {
    const rows = m.rows.map((r, idx) => idx === i ? { ...r, ...patch } : r);
    return {
      ...m, rows,
      nominal: m.nominalTouched ? m.nominal : hitungNominalReturDefault(rows, m.faktorNeto, m.faktorPpn, m.sisaNilaiMaks),
    };
  });
  const submitAjukanJual = async () => {
    const m = ajukanJual;
    if (!m?.sale) return;
    const dipilih = m.rows.filter(r => r.checked && r.qtyRetur > 0);
    if (dipilih.length === 0) { toast.error("Pilih minimal 1 barang untuk diretur"); return; }
    if (dipilih.some(r => !r.alasan.trim())) { toast.error("Alasan retur wajib diisi tiap barang yang dipilih"); return; }
    // Akun kas hanya wajib untuk pengembalian UANG — "tukar unit" tidak
    // mengeluarkan uang hari itu; nilainya ditahan jadi kredit pelanggan.
    if (m.jenisKembali !== "tukar_unit" && !m.akun) { toast.error("Pilih akun pengembalian uang"); return; }
    // no. 33 (Bos 19 Okt 2026): tagihan bertempo yang belum lunas menahan
    // KEDUA bentuk pengembalian — diperiksa sebelum cabang per jenis.
    const tagihanAjukan = syaratTagihanRetur(m.sale);
    if (tagihanAjukan.ditahan) { toast.error(tagihanAjukan.alasan); return; }
    if (m.jenisKembali === "tukar_unit" && !syaratTukarUnit(m.sale).boleh) {
      toast.error(syaratTukarUnit(m.sale).alasan); return;
    }
    if (!(m.nominal > 0)) { toast.error("Nominal kembali harus lebih dari 0"); return; }
    setAjukanJual(x => ({ ...x, submitting: true }));
    try {
      const { data } = await api.post("/retur/penjualan", {
        sale_id: m.sale.id,
        items: dipilih.map(r => ({ index_item: r.index, qty: r.qtyRetur, alasan: r.alasan.trim() })),
        nominal_kembali: m.nominal,
        jenis_kembali: m.jenisKembali,
        metode_kembali: m.jenisKembali === "tukar_unit" ? "" : m.akun,
        catatan: m.catatan || "",
      });
      toast.success(data.message || "Retur penjualan diajukan — menunggu approval");
      setAjukanJual(null);
      loadJual(statusJual);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengajukan retur");
      setAjukanJual(x => ({ ...x, submitting: false }));
    }
  };

  const openAjukanBeli = () => {
    setKasBankError("");
    setAjukanBeli({
      step: "cari", poQuery: "", poTanggal: "", poResults: [], searching: false,
      // Bawaan "" = ditentukan NANTI (permintaan owner 29 Agu 2026). Ini bawaan
      // yang benar: saat barang rusak ditemukan, staf gudang biasanya belum tahu
      // supplier mau ganti dalam bentuk apa — memaksa memilih di detik itu hanya
      // melahirkan tebakan yang harus dikoreksi belakangan.
      po: null, items: [], kompensasi: "", akun_terima: "", alasan: "", submitting: false,
    });
  };

  const cariPo = async (q, tanggal) => {
    // Guard fungsional: kalau modal sudah ditutup (m === null) selagi fetch
    // berjalan, jangan bikin objek baru — dulu `{...null, searching:true}`
    // menghasilkan objek "zombie" tanpa poResults sehingga modal terbuka
    // sendiri dan render meledak TypeError di poResults.length.
    setAjukanBeli(m => (m ? { ...m, searching: true } : m));
    try {
      // Dengan kata kunci: pakai pencarian retur yang juga menyisir NAMA BARANG
      // dan IMEI (permintaan owner 26 Agu 2026 poin 3). Yang dipegang staf saat
      // barang rusak adalah unitnya — IMEI di dus, atau nama barangnya — bukan
      // nomor PO-nya. Tanpa kata kunci, tetap pakai daftar PO biasa supaya
      // layar tidak kosong saat baru dibuka.
      // R2 (brief owner 8 Sep 2026): tanggal ikut dikirim ke server. Kalau ada
      // kata kunci ATAU tanggal, pakai pencarian retur; tanpa keduanya tetap
      // daftar PO biasa supaya layar tidak kosong saat baru dibuka.
      const tgl = (tanggal || "").trim();
      const { data } = (q || tgl)
        ? await api.get("/retur/pembelian/cari-po", {
            params: { ...(q ? { q } : {}), ...(tgl ? { tanggal: tgl } : {}), limit: 30 } })
        : await api.get("/inventory/po", { params: { limit: 30 } });
      // Retur pembelian hanya masuk akal untuk PO yang sudah menerima sesuatu.
      // Hasil pencarian retur tidak membawa `qty_diterima` di tingkat PO, jadi
      // diturunkan dari itemnya.
      // PO barang trade-in/buyback (brief 22 Sep 2026, H1) tidak diretur ke
      // supplier — backend menolaknya; daftar PO bawaan (`/inventory/po`) masih
      // memuatnya, jadi disaring di sini supaya staf tidak mengisi form lalu ditolak.
      const hasil = Array.isArray(data.items)
        ? data.items.filter(p => p.jenis_po !== "tradein" && p.metode_bayar !== "tradein")
            .filter(p => (p.qty_diterima || 0) > 0
            || (p.items || []).some(i => (i.qty_diterima || 0) > 0))
        : [];
      setAjukanBeli(m => (m ? { ...m, poResults: hasil, searching: false } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencari PO");
      setAjukanBeli(m => (m ? { ...m, poResults: Array.isArray(m.poResults) ? m.poResults : [], searching: false } : m));
    }
  };

  const pilihPo = async (po) => {
    setAjukanBeli(m => (m ? { ...m, step: "form", po: null, items: [] } : m));
    try {
      const [poRes, existingRes] = await Promise.all([
        api.get(`/inventory/po/${po.id}`),
        api.get("/retur/pembelian", { params: { po_id: po.id } }),
      ]);
      await muatKasBank();
      const poDetail = poRes.data;
      // Status yang berarti barangnya SUDAH terikat retur lain. Harus sama
      // dengan `STATUS_AKTIF` di backend — kalau layar memakai daftar yang
      // lebih sempit, ia akan menawarkan qty yang sebenarnya sudah diretur.
      const MENGIKAT = ["menunggu_approval", "memproses", "disetujui",
                        "menunggu_kompensasi", "memproses_kompensasi",
                        "menunggu_konfirmasi", "selesai"];
      const aktif = (existingRes.data?.items || []).filter(e => MENGIKAT.includes(e.status));
      const sudahQty = {};
      const sudahImei = {};
      aktif.forEach(e => (e.items || []).forEach(it => {
        sudahQty[it.sku] = (sudahQty[it.sku] || 0) + (it.qty || 0);
        (it.imeis || []).forEach(im => { sudahImei[im] = true; });
      }));
      const rows = (poDetail.items || []).map(it => {
        const diterima = it.qty_diterima || 0;
        const sudah = sudahQty[it.sku] || 0;
        const sisa = Math.max(0, diterima - sudah);
        // Kontrak backend, dari yang paling kaya ke yang paling miskin:
        // 1. `units_diterima_detail` = [{identifier, status}] — dipakai kalau
        //    ada, karena hanya ini yang memberi tahu unit mana yang MASIH bisa
        //    diretur (backend menerima 'tersedia' dan 'rusak' saja).
        // 2. `units_diterima` = larik IMEI yang BENAR-BENAR sudah diterima
        //    (bisa beda dari `units`, yang cuma daftar IMEI yang diketik saat
        //    PO DIBUAT — untuk PO diterima sebagian ia memuat unit yang belum
        //    pernah masuk gudang).
        // 3. `units` = fallback terakhir untuk respons backend lama.
        // Pada (2) dan (3) statusnya tidak diketahui; unit ditampilkan bisa
        // dipilih seperti sebelumnya — layar tidak boleh MELARANG berdasarkan
        // tebakan, backend tetap penjaga terakhirnya.
        const sumberUnit = Array.isArray(it.units_diterima_detail)
          ? it.units_diterima_detail
          : (Array.isArray(it.units_diterima)
            ? it.units_diterima.map(idf => ({ identifier: idf, status: null }))
            : (it.units || []).map(u => ({ identifier: u.identifier, status: null })));
        const unitCandidates = sumberUnit
          .filter(u => u && u.identifier)
          .filter(u => !sudahImei[u.identifier]);
        return {
          sku: it.sku, nama: it.nama, hasImei: !!it.has_imei,
          diterima, sudah, sisa, unitCandidates, checkedImeis: [], qty: 0,
        };
      });
      setAjukanBeli(m => (m ? { ...m, po: poDetail, items: rows } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat detail PO");
      setAjukanBeli(m => (m ? { ...m, step: "cari" } : m));
    }
  };

  const toggleImeiBeli = (idx, identifier) => setAjukanBeli(m => ({
    ...m,
    items: m.items.map((r, i) => {
      if (i !== idx) return r;
      const sudahCek = r.checkedImeis.includes(identifier);
      if (!sudahCek && r.checkedImeis.length >= r.sisa) {
        toast.error(`Maksimum ${r.sisa} unit untuk ${r.nama}`);
        return r;
      }
      const checkedImeis = sudahCek
        ? r.checkedImeis.filter(x => x !== identifier)
        : [...r.checkedImeis, identifier];
      return { ...r, checkedImeis, qty: checkedImeis.length };
    }),
  }));
  const setQtyBeli = (idx, v) => setAjukanBeli(m => ({
    ...m,
    items: m.items.map((r, i) => i === idx ? { ...r, qty: Math.max(0, Math.min(r.sisa, parseInt(v, 10) || 0)) } : r),
  }));

  const submitAjukanBeli = async () => {
    if (!ajukanBeli?.po) return;
    const dipilih = ajukanBeli.items.filter(r => r.qty > 0);
    if (dipilih.length === 0) { toast.error("Pilih minimal 1 barang untuk diretur"); return; }
    if (!ajukanBeli.alasan.trim()) { toast.error("Alasan retur wajib diisi"); return; }
    if (ajukanBeli.kompensasi === "uang_kembali" && !ajukanBeli.akun_terima) {
      toast.error("Pilih akun penerima uang untuk kompensasi uang kembali"); return;
    }
    if (ajukanBeli.kompensasi === "potong_utang" && (ajukanBeli.utangAktif || []).length > 1
        && !ajukanBeli.utang_id) {
      toast.error("PO ini punya lebih dari satu tagihan aktif — pilih tagihan yang dipotong"); return;
    }
    const belumLengkapImei = dipilih.find(r => r.hasImei && r.checkedImeis.length !== r.qty);
    if (belumLengkapImei) { toast.error(`Pilih IMEI/SN untuk ${belumLengkapImei.nama}`); return; }
    setAjukanBeli(m => ({ ...m, submitting: true }));
    try {
      const { data } = await api.post("/retur/pembelian", {
        po_id: ajukanBeli.po.id,
        items: dipilih.map(r => ({
          sku: r.sku, qty: r.qty, imeis: r.hasImei ? r.checkedImeis : undefined,
        })),
        alasan: ajukanBeli.alasan.trim(),
        // Kosong dikirim sebagai `undefined`, BUKAN "" — backend membedakan
        // "belum ditentukan" (null) dari nilai yang tidak dikenal.
        kompensasi: ajukanBeli.kompensasi || undefined,
        akun_terima: ajukanBeli.kompensasi === "uang_kembali" ? ajukanBeli.akun_terima : undefined,
        utang_id: ajukanBeli.kompensasi === "potong_utang" && ajukanBeli.utang_id
          ? ajukanBeli.utang_id : undefined,
      });
      toast.success(data.message || "Retur pembelian diajukan — menunggu approval");
      setAjukanBeli(null);
      loadBeli(statusBeli);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengajukan retur pembelian");
      setAjukanBeli(m => ({ ...m, submitting: false }));
    }
  };

  const totalQtyDiajukan = (it) => (it.items || []).reduce((a, x) => a + (x.qty || 0), 0);

  return (
    <div className="p-4 md:p-8 max-w-[1200px]">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">POS &amp; Sales</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Retur Penjualan &amp; Pembelian</h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">
          <Info className="w-3.5 h-3.5 inline mb-0.5 mr-1 text-slate-400" />
          Pengajuan retur murni dokumen — jurnal &amp; stok baru bergerak setelah disetujui SPV/admin.
        </p>
      </header>

      {!bisaLihat ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="rt-tidak-punya-akses">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Anda tidak punya akses ke Retur Penjualan &amp; Pembelian</p>
          <p className="text-sm text-slate-500 mt-1">Hubungi admin untuk meminta izin modul &quot;Retur&quot;.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1">
              <button onClick={() => selectTab("penjualan")} data-testid="rt-tab-penjualan"
                className={`h-9 px-4 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                  tab === "penjualan" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                <RotateCcw className="w-4 h-4" /> Retur Penjualan
              </button>
              <button onClick={() => selectTab("pembelian")} data-testid="rt-tab-pembelian"
                className={`h-9 px-4 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                  tab === "pembelian" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                <Truck className="w-4 h-4" /> Retur Pembelian
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setShowKredit(true); muatKredit(); }}
                data-testid="rt-kredit-pelanggan"
                title="Kredit pelanggan dari retur tukar unit, dan apakah saldonya cocok dengan COA 2150"
                className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
                Kredit Pelanggan
              </button>
              <select value={tab === "penjualan" ? statusJual : statusBeli}
                onChange={e => (tab === "penjualan" ? setStatusJual(e.target.value) : setStatusBeli(e.target.value))}
                data-testid="rt-status-filter"
                className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[190px]">
                <option value="">Semua Status</option>
                <option value="menunggu_approval">Menunggu Approval</option>
                <option value="disetujui">Disetujui</option>
                {tab === "pembelian" && <option value="menunggu_kompensasi">Perlu Pilih Kompensasi</option>}
                {tab === "pembelian" && <option value="menunggu_konfirmasi">Menunggu Kompensasi Diterima</option>}
                <option value="selesai">Selesai</option>
                <option value="ditolak">Ditolak</option>
              </select>
              {tab === "penjualan" && bisaAjukanBeli && (
                <button onClick={openAjukanJual} data-testid="rt-ajukan-jual"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-medium bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4" /> Ajukan Retur Penjualan
                </button>
              )}
              {tab === "pembelian" && bisaAjukanBeli && (
                <button onClick={openAjukanBeli} disabled={!bisaCariPo} data-testid="rt-ajukan-beli"
                  title={!bisaCariPo ? "Butuh izin Inventory > Pembelian (lihat) untuk mencari PO — minta admin di User Management" : ""}
                  className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-medium ${
                    bisaCariPo ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-300 cursor-not-allowed"}`}>
                  <Plus className="w-4 h-4" /> Ajukan Retur Pembelian
                </button>
              )}
            </div>
          </div>

          {loadingList ? (
            <LoadingState loading data-testid="rt-list-loading" />
          ) : tab === "penjualan" ? (
            listJual.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-16 text-center" data-testid="rt-empty-penjualan">
                <RotateCcw className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="font-display font-semibold text-slate-900">Belum ada retur penjualan</p>
                <p className="text-sm text-slate-500 mt-1">Ajukan lewat tombol "Ajukan Retur Penjualan" di atas, atau dari panel detail transaksi di halaman Penjualan.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {listJual.map(it => (
                  <button key={it.id} type="button" onClick={() => openDetail("penjualan", it.id)}
                    data-testid={`rt-row-penjualan-${it.id}`}
                    className="text-left bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-colors rounded-xl p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="font-display font-semibold text-slate-900">{it.code}</p>
                      <StatusBadge status={it.status} />
                    </div>
                    <p className="text-sm text-slate-700">{it.sale_nama_pelanggan || "-"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{it.tanggal} · {it.sale_branch || "-"}</p>
                    <p className="text-xs text-slate-500 mt-1">Metode: {metodeReturJual(it)}</p>
                    {/* Brief owner 4 Sep butir 18: retur yang sudah disetujui masih
                        menunggu penyerahan uang/kredit ke pelanggan. Tanpa penanda ini
                        ia terlihat sama persis dengan yang sudah tuntas. */}
                    {it.status === "disetujui" && (
                      <p className="text-[11px] font-medium text-sky-700 mt-1">Perlu tindakan — tandai selesai setelah diserahkan</p>
                    )}
                    <div className="flex items-end justify-between pt-3 mt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 inline-flex items-center gap-1"><User className="w-3 h-3" /> {it.created_by_name}</p>
                      <p className="font-display font-bold text-rose-700">{idr(it.nominal_kembali)}</p>
                    </div>
                  </button>
                ))}
              </div>
            )
          ) : (
            listBeli.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-16 text-center" data-testid="rt-empty-pembelian">
                <Truck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="font-display font-semibold text-slate-900">Belum ada retur pembelian</p>
                <p className="text-sm text-slate-500 mt-1">Klik &quot;Ajukan Retur Pembelian&quot; untuk mulai.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {listBeli.map(it => (
                  <button key={it.id} type="button" onClick={() => openDetail("pembelian", it.id)}
                    data-testid={`rt-row-pembelian-${it.id}`}
                    className="text-left bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-colors rounded-xl p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="font-display font-semibold text-slate-900">{it.code}</p>
                      <StatusBadge status={it.status} />
                    </div>
                    <p className="text-sm text-slate-700">{it.supplier_nama || "-"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{it.tanggal} · PO {it.po_code}</p>
                    <div className="flex items-end justify-between pt-3 mt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 inline-flex items-center gap-1"><User className="w-3 h-3" /> {it.created_by_name}</p>
                      <p className="font-display font-bold text-rose-700">
                        {it.status === "disetujui" ? idr(it.nilai_total) : `${totalQtyDiajukan(it)} unit diajukan`}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )
          )}
        </>
      )}

      {/* DETAIL MODAL */}
      {detailModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setDetailModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}
            data-testid="rt-detail-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                <h3 className="font-display font-semibold">
                  Detail Retur {detailModal.jenis === "penjualan" ? "Penjualan" : "Pembelian"}
                </h3>
              </div>
              <button onClick={() => setDetailModal(null)} className="p-2 hover:bg-white/20 rounded-lg" data-testid="rt-detail-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {detailModal.loading ? (
              <div className="p-6"><LoadingState loading jumlahKartu={1} data-testid="rt-detail-loading" /></div>
            ) : (() => {
              const d = detailModal.data;
              const jenis = detailModal.jenis;
              // K0-3: pemilih tagihan tampil kalau PO punya >1 tagihan aktif,
              // ATAU tagihan yang dipilih saat pengajuan sudah tidak aktif lagi
              // (dilunasi/terpotong retur lain) — tanpa itu approver buntu.
              const kandidatUtang = d.utang_kandidat || [];
              const pilihanTersimpanBasi = !!d.utang_id && !kandidatUtang.some(k => k.id === d.utang_id);
              const perluPilihUtang = jenis === "pembelian" && d.status === "menunggu_approval"
                && d.kompensasi === "potong_utang"
                && (kandidatUtang.length > 1 || (pilihanTersimpanBasi && kandidatUtang.length > 0));
              // Pilihan PENGAJU yang masih aktif menjadi isian awal (tinjauan 24
              // Sep 2026): tanpa ini approver wajib memilih ulang dari nol dan
              // bisa tanpa sengaja memotong tagihan yang berbeda dari yang
              // diajukan. `??` — pilihan "— pilih tagihan —" yang disengaja
              // approver ("") tidak ditimpa kembali.
              const utangDipilih = utangApprove[d.id]
                ?? (d.utang_id && !pilihanTersimpanBasi ? d.utang_id : "");
              // I1 (22 Sep 2026): pemilik kredit tukar unit dihitung SERVER
              // (`kredit_identitas`, aturan yang sama dengan approve). Tidak sah =
              // tombol Setujui mati dengan alasan tertulis, bukan klik yang
              // berujung 400.
              const identKredit = jenis === "penjualan" ? d.kredit_identitas : null;
              const identTidakSah = !!identKredit && identKredit.sah === false;
              // 29a (owner 25 Sep 2026) + no. 33 (19 Okt 2026): retur atas nota
              // BERTEMPO yang tagihannya belum lunas ditahan saat approve —
              // UANG maupun tukar unit. Status dihitung SERVER (`tagihan_retur`,
              // aturan yang sama dengan approve); `tagihan_tukar_unit` = nama
              // lama yang masih dikirim server.
              const tagihanTukar = jenis === "penjualan"
                ? (d.tagihan_retur ?? d.tagihan_tukar_unit) : null;
              const tagihanDitahan = !!tagihanTukar && tagihanTukar.ditahan === true;
              // Tinjauan gelombang 3: nota yang berubah sejak retur diajukan (baris
              // dibuang/diganti, harga/qty turun) menahan approve — dinilai SERVER
              // (`cocok_nota`, fungsi yang sama dengan penjaga approve).
              const notaBerubah = jenis === "penjualan" && !!d.cocok_nota && d.cocok_nota.cocok === false;
              return (
                <div className="p-6 space-y-5">
                  <div className="flex items-start justify-between border-b border-slate-100 pb-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">{jenis === "penjualan" ? "Pelanggan" : "Supplier"}</p>
                      <p className="font-display text-lg font-bold text-slate-900">
                        {jenis === "penjualan" ? (d.sale_nama_pelanggan || "-") : (d.supplier_nama || "-")}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">{d.code} · {d.tanggal}</p>
                    </div>
                    <div className="text-right">
                      <StatusBadge status={d.status} />
                      {jenis === "pembelian" && <p className="text-xs text-slate-500 mt-1">PO {d.po_code}</p>}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Barang Diretur</p>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500">
                          <tr>
                            <th className="text-left py-2 px-3">Barang</th>
                            <th className="text-right py-2 px-3">Qty</th>
                            <th className="text-left py-2 px-3">Alasan</th>
                            {jenis === "penjualan" && <th className="text-left py-2 px-3">Stok</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(d.items || []).map((it, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="py-2 px-3">
                                <p className="font-medium text-slate-900">{it.nama_barang || it.sku}</p>
                                {(it.imei || (it.imeis || []).length > 0) && (
                                  <p className="text-[11px] font-mono text-slate-500">
                                    IMEI {it.imei || (it.imeis || []).join(", ")}
                                  </p>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right font-mono">{it.qty}</td>
                              <td className="py-2 px-3 text-slate-600 text-xs">{it.alasan || d.alasan || "-"}</td>
                              {jenis === "penjualan" && (
                                <td className="py-2 px-3">
                                  {it.stok_dikembalikan === undefined ? (
                                    <span className="text-[11px] text-slate-400">-</span>
                                  ) : it.stok_dikembalikan ? (
                                    <span className="text-[11px] text-emerald-700">Dikembalikan</span>
                                  ) : (
                                    // Alasannya DITAMPILKAN, bukan disembunyikan di tooltip.
                                    // Untuk baris paket, alasan inilah yang memberi tahu staf
                                    // bahwa barangnya masih tercatat terjual dan apa yang harus
                                    // dilakukan — tidak berguna kalau harus di-hover dulu.
                                    <div className="max-w-[22rem]">
                                      <span className="text-[11px] font-medium text-amber-700">Tidak dikembalikan</span>
                                      {it.alasan_stok && (
                                        <p className="text-[11px] text-amber-800 mt-0.5 whitespace-pre-line"
                                          data-testid="retur-alasan-stok">
                                          {it.alasan_stok}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1.5">
                    {jenis === "penjualan" ? (
                      <>
                        <div className="flex justify-between"><span className="text-slate-500">Nominal Kembali</span><span className="font-mono font-bold">{idr(d.nominal_kembali)}</span></div>
                        {/* Retur "tukar unit" tidak punya akun kas sama sekali —
                            barisnya dulu tampil "-" dan tidak memberi tahu apa pun
                            tentang bentuk pengembaliannya. Satu label, satu sumber. */}
                        <div className="flex justify-between"><span className="text-slate-500">Metode Pengembalian</span><span>{metodeReturJual(d)}</span></div>
                        {identKredit && (
                          <div className="flex justify-between gap-4" data-testid="rt-kredit-identitas">
                            <span className="text-slate-500 shrink-0">Kredit milik</span>
                            <span className="text-right">{identKredit.nama || "-"} · No HP {identKredit.no_hp || "-"}</span>
                          </div>
                        )}
                        {identTidakSah && (
                          <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2"
                             data-testid="rt-kredit-identitas-tidak-sah">
                            Retur ini diajukan tanpa identitas pelanggan yang lengkap ({identKredit.alasan}).
                            Kredit tukar unit hanya bisa dipakai pelanggan dengan nama DAN No HP yang sama,
                            jadi tidak bisa disetujui — tolak, lalu ajukan ulang setelah No HP nota
                            dilengkapi, atau sebagai pengembalian uang.
                          </p>
                        )}
                        {tagihanDitahan && (
                          <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2"
                             data-testid="rt-tukar-tagihan-ditahan">
                            {tagihanTukar.alasan} Retur ini bisa disetujui setelah tagihannya lunas — atau
                            tolak retur ini.
                          </p>
                        )}
                        {notaBerubah && (
                          <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2"
                             data-testid="rt-nota-berubah">
                            Nota penjualannya sudah berubah sejak retur ini diajukan: {d.cocok_nota.alasan}.
                            Retur ini tidak bisa disetujui — tolak, lalu ajukan ulang dari isi nota yang sekarang.
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Kompensasi</span>
                          <span>{KOMPENSASI_LABEL[d.kompensasi] || (
                            <span className="text-sky-700 font-medium">Belum ditentukan</span>)}</span>
                        </div>
                        {d.kompensasi === "uang_kembali" && (
                          <div className="flex justify-between"><span className="text-slate-500">Akun Penerima</span><span>{d.kompensasi_akun_nama || d.akun_terima_nama || "-"}</span></div>
                        )}
                        {d.kompensasi_cn_id && (
                          <div className="flex justify-between"><span className="text-slate-500">Credit Note</span><span className="text-emerald-700">dibuat otomatis</span></div>
                        )}
                        {["disetujui", "menunggu_kompensasi", "menunggu_konfirmasi", "selesai"].includes(d.status) && (
                          <div className="flex justify-between"><span className="text-slate-500">Nilai Total</span><span className="font-mono font-bold">{idr(d.total_retur || d.nilai_total)}</span></div>
                        )}
                        {d.status === "selesai" && d.kompensasi_diterima_by && (
                          <div className="flex justify-between"><span className="text-slate-500">Kompensasi diterima</span><span>{d.kompensasi_diterima_by}</span></div>
                        )}
                        {(d.kompensasi_riwayat || []).length > 1 && (
                          <div className="pt-1 border-t border-slate-100">
                            <p className="text-[11px] text-slate-500 mb-0.5">Riwayat perubahan kompensasi</p>
                            {d.kompensasi_riwayat.map((h, i) => (
                              <p key={i} className="text-[11px] text-slate-600">
                                {KOMPENSASI_LABEL[h.kompensasi] || h.kompensasi}
                                {h.mengganti ? ` (menggantikan ${KOMPENSASI_LABEL[h.mengganti] || h.mengganti})` : ""}
                                {h.by ? ` — ${h.by}` : ""}
                              </p>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {d.catatan && <div className="flex justify-between gap-4"><span className="text-slate-500 shrink-0">Catatan</span><span className="text-right italic text-slate-600">{d.catatan}</span></div>}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Diajukan oleh</p>
                      <p className="text-slate-900">{d.created_by_name}</p>
                    </div>
                    {d.status === "disetujui" && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Disetujui oleh</p>
                        <p className="text-slate-900">{d.approved_by_name} <span className="text-xs text-slate-400">· {formatTanggal(d.approved_at, { denganJam: true })}</span></p>
                      </div>
                    )}
                    {jenis === "penjualan" && d.status === "selesai" && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Diselesaikan oleh</p>
                        <p className="text-slate-900">{d.selesai_by_name || d.selesai_by} <span className="text-xs text-slate-400">· {formatTanggal(d.selesai_at, { denganJam: true })}</span></p>
                        {d.selesai_catatan && <p className="text-xs italic text-slate-600 mt-0.5">{d.selesai_catatan}</p>}
                      </div>
                    )}
                    {d.status === "ditolak" && (
                      <div className="col-span-2">
                        <p className="text-xs text-rose-600 uppercase tracking-wider mb-0.5">Ditolak oleh {d.rejected_by_name}</p>
                        <p className="text-slate-700 text-sm">{d.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  {perluPilihUtang && bisaKelola() && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="rt-approve-utang-box">
                      <p className="text-xs font-semibold text-amber-900">
                        {pilihanTersimpanBasi
                          ? "Tagihan yang dipilih saat pengajuan sudah tidak aktif — pilih tagihan lain yang dipotong"
                          : d.utang_id
                            ? `PO ini punya ${kandidatUtang.length} tagihan aktif — terisi pilihan pengaju; ganti kalau perlu`
                            : `PO ini punya ${kandidatUtang.length} tagihan aktif — pilih tagihan yang dipotong`}
                      </p>
                      <select value={utangDipilih} data-testid="rt-approve-utang"
                        onChange={e => setUtangApprove(m => ({ ...m, [d.id]: e.target.value }))}
                        className="h-10 w-full px-3 rounded-lg border border-slate-300 text-sm bg-white">
                        <option value="">— pilih tagihan —</option>
                        {kandidatUtang.map(k => (
                          <option key={k.id} value={k.id}>
                            {k.code} · No. DO/Invoice {k.no_invoice || "-"} · sisa {idr(k.sisa)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-2 border-t border-slate-100 flex-wrap gap-2">
                    <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {d.tanggal}</div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => bukaCetak(jenis, d.id, d.warehouse_id)} data-testid={`rt-cetak-${d.id}`}
                        disabled={!["disetujui", "menunggu_kompensasi", "menunggu_konfirmasi", "selesai"].includes(d.status)}
                        title={!["disetujui", "menunggu_kompensasi", "menunggu_konfirmasi", "selesai"].includes(d.status) ? "Tersedia setelah disetujui" : ""}
                        className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium inline-flex items-center gap-1.5">
                        <Printer className="w-4 h-4" /> Cetak Faktur Retur
                      </button>
                      {d.status === "menunggu_approval" && bisaKelola() && (
                        <>
                          <button onClick={() => setTolakModal({ jenis, id: d.id, code: d.code, alasan: "", submitting: false })}
                            data-testid={`rt-tolak-${d.id}`}
                            className="h-10 px-4 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                            <X className="w-4 h-4" /> Tolak
                          </button>
                          <button onClick={() => approve(jenis, d, utangDipilih || undefined)}
                            data-testid={`rt-approve-${d.id}`}
                            disabled={(perluPilihUtang && !utangDipilih) || identTidakSah || tagihanDitahan || notaBerubah}
                            title={perluPilihUtang && !utangDipilih ? "Pilih tagihan yang dipotong dulu"
                              : identTidakSah ? "Identitas pelanggan tidak lengkap — lihat keterangan di atas"
                                : tagihanDitahan ? "Tagihan nota bertempo belum lunas — lihat keterangan di atas"
                                  : notaBerubah ? "Nota penjualannya sudah berubah — lihat keterangan di atas" : ""}
                            className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium inline-flex items-center gap-1.5">
                            <Check className="w-4 h-4" /> Setujui
                          </button>
                        </>
                      )}
                      {/* Kompensasi menyusul (permintaan owner 26 & 29 Agu 2026).
                          Tombolnya hanya muncul selama retur belum dikonfirmasi —
                          gerbang yang SAMA dengan backend, supaya tidak ada klik
                          yang berujung 400. */}
                      {jenis === "pembelian" && bisaKelola()
                        && ["menunggu_kompensasi", "menunggu_konfirmasi"].includes(d.status) && (
                        <button onClick={() => setKompensasiModal({
                                  id: d.id, code: d.code, nilai: d.total_retur || d.nilai_total,
                                  kompensasi: d.kompensasi || "", akun_terima_id: "",
                                  utang_id: "", utang_kandidat: d.utang_kandidat || [],
                                  mengubah: d.status === "menunggu_konfirmasi", submitting: false })}
                          data-testid={`rt-kompensasi-${d.id}`}
                          className="h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
                          <Check className="w-4 h-4" />
                          {d.status === "menunggu_kompensasi" ? "Pilih Kompensasi" : "Ubah Kompensasi"}
                        </button>
                      )}
                      {/* Gerbangnya SAMA dengan backend (`selesaikan_retur_penjualan`
                          hanya menerima status 'disetujui' + izin retur.update) —
                          tombol yang ujungnya 409/403 tidak boleh muncul. */}
                      {jenis === "penjualan" && bisaKelola() && d.status === "disetujui" && (
                        <button onClick={() => selesaikanReturJual(d)}
                          data-testid={`rt-selesai-${d.id}`}
                          className="h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
                          <Check className="w-4 h-4" /> Tandai Selesai
                        </button>
                      )}
                      {jenis === "pembelian" && bisaKelola() && d.status === "menunggu_konfirmasi" && (
                        <button onClick={() => konfirmasiKompensasi(d)}
                          data-testid={`rt-konfirmasi-${d.id}`}
                          className="h-10 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
                          <Check className="w-4 h-4" /> Kompensasi Sudah Diterima
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* TOLAK MODAL */}
      {/* Pilih / ubah kompensasi retur pembelian (permintaan owner 26 & 29 Agu 2026).
          MENGUBAH kompensasi tidak menulis ulang jurnal lama — backend
          membalikkannya dengan entri lawan lalu memposting yang baru, dan
          keduanya tetap terlihat di buku besar. Itu dikatakan terus terang di
          sini supaya yang menekan tombolnya tahu apa yang terjadi. */}
      {/* Kredit pelanggan + REKONSILIASI dua buku. Saldo COA 2150 harus selalu
          sama dengan jumlah sisa kredit; kalau berbeda, salah satunya bocor.
          Panel ini yang membuat selisihnya ketahuan cepat, bukan berbulan-bulan
          kemudian (pelajaran selisih Rp 12 juta persediaan). */}
      {showKredit && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
             onClick={() => setShowKredit(false)}>
          <div onClick={e => e.stopPropagation()} data-testid="rt-kredit-modal"
               className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Kredit Pelanggan (Tukar Unit)</h3>
              <button onClick={() => setShowKredit(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {!kredit ? (
                <p className="text-sm text-slate-400 text-center py-8">Memuat...</p>
              ) : (
                <>
                  <div className={`rounded-lg border p-3 ${
                    kredit.rekon?.cocok ? "bg-emerald-50 border-emerald-200"
                                        : "bg-rose-50 border-rose-200"}`}
                       data-testid="rt-kredit-rekon">
                    <p className={`text-sm font-semibold ${
                      kredit.rekon?.cocok ? "text-emerald-800" : "text-rose-800"}`}>
                      {kredit.rekon?.cocok ? "Dua buku COCOK" : "Dua buku TIDAK COCOK"}
                    </p>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div><span className="text-slate-500">Saldo COA 2150</span>
                        <p className="font-mono">{idr(kredit.rekon?.saldo_coa || 0)}</p></div>
                      <div><span className="text-slate-500">Total sisa kredit</span>
                        <p className="font-mono">{idr(kredit.rekon?.total_sisa_kredit || 0)}</p></div>
                      <div><span className="text-slate-500">Selisih</span>
                        <p className="font-mono">{idr(kredit.rekon?.selisih || 0)}</p></div>
                    </div>
                    <p className="text-[11px] mt-2 text-slate-600">{kredit.rekon?.keterangan}</p>
                  </div>

                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2">Pelanggan</th>
                          <th className="text-left px-3 py-2">Dari Retur</th>
                          <th className="text-right px-3 py-2">Nilai</th>
                          <th className="text-right px-3 py-2">Terpakai</th>
                          <th className="text-right px-3 py-2">Sisa</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(kredit.items || []).length === 0 ? (
                          <tr><td colSpan={5} className="p-8 text-center text-sm text-slate-400">
                            Belum ada kredit pelanggan yang aktif
                          </td></tr>
                        ) : kredit.items.map(k => (
                          <tr key={k.id} data-testid={`rt-kredit-${k.id}`}>
                            <td className="px-3 py-2 align-top">
                              <p className="text-slate-900">{k.pelanggan_nama}</p>
                              {k.no_hp && <p className="text-[10px] text-slate-400">{k.no_hp}</p>}
                              {/* I1: kredit tanpa No HP sah TIDAK cocok dengan nota mana
                                  pun sampai approver menetapkan nomornya. */}
                              {!k.no_hp_kunci && k.status === "aktif" && (
                                <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium"
                                      data-testid={`rt-kredit-tertahan-${k.id}`}>
                                  Tertahan — No HP belum sah
                                </span>
                              )}
                              {(k.riwayat_no_hp || []).map((r, i) => (
                                <p key={`hp${i}`} className="text-[10px] text-slate-500">
                                  No HP {r.dari || "-"} → {r.ke} · {r.oleh_nama || r.oleh} · {r.alasan}
                                </p>
                              ))}
                              {(k.riwayat_nama || []).map((r, i) => (
                                <p key={`nm${i}`} className="text-[10px] text-slate-500">
                                  Nama {r.dari || "-"} → {r.ke} · {r.oleh_nama || r.oleh} · {r.alasan}
                                </p>
                              ))}
                              {/* Tombol SELALU terlihat (bukan hover) — di perangkat
                                  sentuh tidak ada hover. Gerbang sama dengan server. */}
                              {bisaKelola() && k.status === "aktif" && (k.sisa || 0) > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  <button onClick={() => bukaPindah(k)} data-testid={`rt-kredit-pindah-${k.id}`}
                                    className="h-7 px-2 rounded border border-slate-300 text-[11px] text-slate-700 hover:bg-slate-50">
                                    {k.no_hp_kunci ? "Ganti No HP" : "Tetapkan No HP"}
                                  </button>
                                  {k.no_hp_kunci && k.kartu_pelanggan && !k.kartu_pelanggan.sama && (
                                    <button onClick={() => selaraskanNama(k)} data-testid={`rt-kredit-selaraskan-${k.id}`}
                                      title={`Nama di kartu pelanggan: ${k.kartu_pelanggan.nama}`}
                                      className="h-7 px-2 rounded border border-slate-300 text-[11px] text-slate-700 hover:bg-slate-50">
                                      Samakan nama dgn kartu: {k.kartu_pelanggan.nama}
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-slate-500">{k.sumber_retur_code}</td>
                            <td className="px-3 py-2 text-right font-mono">{idr(k.nilai)}</td>
                            <td className="px-3 py-2 text-right font-mono text-slate-500">{idr(k.terpakai)}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold">{idr(k.sisa)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Kredit ini dipakai pelanggan lewat metode pembayaran
                    &quot;Kredit Retur&quot; saat membuat nota baru — hanya oleh pelanggan
                    dengan nama DAN No HP yang sama dengan nota retur asal.
                  </p>
                  {bisaKelola() && (
                    <button onClick={isiKunciKredit} data-testid="rt-kredit-isi-kunci"
                      className="h-8 px-3 rounded-lg border border-slate-300 text-xs text-slate-700 hover:bg-slate-50">
                      Periksa kunci No HP kredit lama
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ganti / tetapkan No HP kredit (I1, brief 22 Sep 2026). */}
      {pindahModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4"
             onClick={() => !pindahModal.submitting && setPindahModal(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="rt-kredit-pindah-modal"
               className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-5 space-y-3">
            <h3 className="font-display font-bold text-lg">
              {pindahModal.kredit.no_hp_kunci ? "Ganti No HP Kredit" : "Tetapkan No HP Kredit"}
            </h3>
            <p className="text-xs text-slate-600">
              Kredit {pindahModal.kredit.sumber_retur_code || ""} · <b>{pindahModal.kredit.pelanggan_nama}</b> ·
              sisa {idr(pindahModal.kredit.sisa || 0)}. Sesudah dipindah, kredit hanya bisa dipakai dengan
              nama ini + No HP baru. Tercatat di riwayat & dilaporkan ke owner.
            </p>
            <Field l="No HP baru">
              <input value={pindahModal.noHpBaru} data-testid="rt-kredit-pindah-hp"
                onChange={e => setPindahModal(m => ({ ...m, noHpBaru: e.target.value }))}
                className={inp} placeholder="08xx…" />
            </Field>
            {pindahModal.noHpBaru.trim() && !periksaKunciHp(pindahModal.noHpBaru).kunci && (
              <p className="text-[11px] text-rose-700">{pesanHpTidakSah(periksaKunciHp(pindahModal.noHpBaru).alasan)}</p>
            )}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1">Bukti kepemilikan (dari pelanggan)</p>
              <div className="flex flex-wrap gap-2">
                {pindahModal.kredit.no_hp_kunci && (
                  <button type="button" data-testid="rt-kredit-pindah-bukti-hp"
                    onClick={() => setPindahModal(m => ({ ...m, bukti: "hp", nilaiBukti: "" }))}
                    className={`h-8 px-3 rounded-lg text-xs border ${pindahModal.bukti === "hp" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700"}`}>
                    No HP lama yang disebut pelanggan
                  </button>
                )}
                <button type="button" data-testid="rt-kredit-pindah-bukti-kode"
                  onClick={() => setPindahModal(m => ({ ...m, bukti: "kode", nilaiBukti: "" }))}
                  className={`h-8 px-3 rounded-lg text-xs border ${pindahModal.bukti === "kode" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700"}`}>
                  Nomor struk retur (RTJ-…)
                </button>
              </div>
              <input value={pindahModal.nilaiBukti} data-testid="rt-kredit-pindah-bukti"
                onChange={e => setPindahModal(m => ({ ...m, nilaiBukti: e.target.value }))}
                className={inp + " mt-2"}
                placeholder={pindahModal.bukti === "hp" ? "No HP LAMA menurut pelanggan" : "Nomor di struk retur pelanggan"} />
            </div>
            <Field l="Alasan (wajib)">
              <input value={pindahModal.alasan} data-testid="rt-kredit-pindah-alasan"
                onChange={e => setPindahModal(m => ({ ...m, alasan: e.target.value }))}
                className={inp} placeholder="mis. pelanggan ganti nomor, datang langsung dengan struk" />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setPindahModal(null)} disabled={pindahModal.submitting}
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={kirimPindah} data-testid="rt-kredit-pindah-simpan"
                disabled={pindahModal.submitting || !hpSah(pindahModal.noHpBaru)
                  || !pindahModal.alasan.trim() || !pindahModal.nilaiBukti.trim()}
                className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium">
                {pindahModal.submitting ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {kompensasiModal && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
             onClick={() => setKompensasiModal(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="rt-kompensasi-modal"
               className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-lg">
                  {kompensasiModal.mengubah ? "Ubah" : "Pilih"} Kompensasi Retur
                </h3>
                <p className="text-xs text-slate-500 font-mono">{kompensasiModal.code}</p>
              </div>
              <button onClick={() => setKompensasiModal(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Nilai retur</span>
                <span className="font-mono font-bold">{idr(kompensasiModal.nilai)}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Nilai ini sedang parkir di <b>Piutang Retur Pembelian</b> dan akan pindah ke
                tujuan yang dipilih di bawah.
              </p>
            </div>

            {kompensasiModal.mengubah && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-900">
                Kompensasi sebelumnya akan <b>dibalik dengan jurnal lawan</b>, bukan dihapus —
                keduanya tetap terlihat di buku besar. Kalau sebelumnya Kredit DOA, Credit
                Note-nya ikut dibatalkan.
              </div>
            )}

            <div className="space-y-2">
              {[
                ["potong_utang", "Potong Utang PO", "Mengurangi utang usaha atas PO ini. Butuh utang yang masih aktif dan cukup."],
                ["uang_kembali", "Uang Kembali", "Supplier mengembalikan tunai/transfer ke akun kas atau bank."],
                ["kredit_doa", "Kredit DOA di Supplier", "Nilainya ditahan sebagai Credit Note — dipakai memotong tagihan pembelian berikutnya saat unit tukarannya datang."],
              ].map(([nilai, judul, ket]) => (
                <label key={nilai} data-testid={`rt-komp-opsi-${nilai}`}
                       className={`flex gap-2 items-start p-3 rounded-lg border cursor-pointer ${
                         kompensasiModal.kompensasi === nilai
                           ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <input type="radio" name="rt-komp-pilih" className="mt-0.5"
                         checked={kompensasiModal.kompensasi === nilai}
                         onChange={() => setKompensasiModal(x => ({ ...x, kompensasi: nilai }))} />
                  <span>
                    <span className="text-sm font-medium text-slate-900 block">{judul}</span>
                    <span className="text-[11px] text-slate-500">{ket}</span>
                  </span>
                </label>
              ))}
            </div>

            {kompensasiModal.kompensasi === "uang_kembali" && (
              <Field l="Akun Penerima Uang">
                <select value={kompensasiModal.akun_terima_id} data-testid="rt-komp-akun"
                        onChange={e => setKompensasiModal(x => ({ ...x, akun_terima_id: e.target.value }))}
                        className={inp + " bg-white"}>
                  <option value="">— pilih akun kas/bank —</option>
                  {kasBank.map(a => <option key={a.id} value={a.id}>{a.name} ({idr(a.balance)})</option>)}
                </select>
              </Field>
            )}

            {/* K0-3: PO bertahap punya satu tagihan per DO. Lebih dari satu =
                wajib dipilih; backend menolak menebak. */}
            {kompensasiModal.kompensasi === "potong_utang" && (kompensasiModal.utang_kandidat || []).length > 1 && (
              <Field l="Tagihan yang dipotong">
                <select value={kompensasiModal.utang_id} data-testid="rt-komp-utang"
                        onChange={e => setKompensasiModal(x => ({ ...x, utang_id: e.target.value }))}
                        className={inp + " bg-white"}>
                  <option value="">— pilih tagihan —</option>
                  {kompensasiModal.utang_kandidat.map(k => (
                    <option key={k.id} value={k.id}>
                      {k.code} · No. DO/Invoice {k.no_invoice || "-"} · sisa {idr(k.sisa)}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setKompensasiModal(null)}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={kirimKompensasi}
                      disabled={kompensasiModal.submitting
                        || (kompensasiModal.kompensasi === "potong_utang"
                            && (kompensasiModal.utang_kandidat || []).length > 1 && !kompensasiModal.utang_id)}
                      data-testid="rt-komp-kirim"
                      className="h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium disabled:opacity-50">
                {kompensasiModal.submitting ? "Menyimpan..." : "Simpan Kompensasi"}
              </button>
            </div>
          </div>
        </div>
      )}

      {tolakModal && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setTolakModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="rt-tolak-modal">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
                <X className="w-4 h-4" /> Tolak Retur {tolakModal.code}
              </h3>
              <button onClick={() => setTolakModal(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <label className="text-xs font-medium text-slate-600">Alasan penolakan *</label>
              <textarea value={tolakModal.alasan} rows={4} autoFocus
                onChange={e => setTolakModal(m => ({ ...m, alasan: e.target.value }))}
                data-testid="rt-tolak-alasan"
                className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                placeholder="Jelaskan alasan retur ini ditolak" />
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setTolakModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={submitTolak} disabled={tolakModal.submitting || !tolakModal.alasan.trim()}
                data-testid="rt-tolak-kirim"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
                {tolakModal.submitting ? "Memproses..." : "Kirim Penolakan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CETAK FAKTUR RETUR MODAL */}
      {cetakModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setCetakModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="rt-cetak-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Printer className="w-4 h-4" /> Cetak Faktur Retur
              </h3>
              <button onClick={() => setCetakModal(null)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {cetakModal.step === "pilih_template" ? (
                <div className="space-y-4">
                  <PemilihTemplate templates={cetakModal.templates} value={cetakModal.templateId}
                    onChange={(id) => setCetakModal(m => ({ ...m, templateId: id }))}
                    jenisLabel={`retur ${cetakModal.jenis}`} testidPrefix="rt-cetak-tpl" />
                  <div className="flex justify-end">
                    <button onClick={() => muatDokumenCetak(cetakModal.jenis, cetakModal.id, cetakModal.templates.find(t => t.id === cetakModal.templateId))}
                      data-testid="rt-cetak-tpl-lanjut"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                      Lanjutkan
                    </button>
                  </div>
                </div>
              ) : cetakModal.step === "kosong" ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="rt-cetak-tpl-kosong">
                  Belum ada template retur {cetakModal.jenis}. Buat dulu di menu Template Faktur &amp; Struk.
                </p>
              ) : cetakModal.loading || !cetakModal.data ? (
                <LoadingState loading jumlahKartu={1} data-testid="rt-cetak-loading" />
              ) : (() => {
                const payload = cetakModal.data;
                const adapted = cetakModal.jenis === "penjualan"
                  ? adaptReturJualUntukCetak(payload.retur, payload.sale)
                  : adaptReturBeliUntukCetak(payload.retur, payload.po);
                return isStruk(payload.template) ? (
                  <StrukPrint data={adapted} template={payload.template} noFaktur={payload.no_faktur} />
                ) : (
                  <FakturPrint data={adapted} template={payload.template} noFaktur={payload.no_faktur} />
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* AJUKAN RETUR PENJUALAN (P18a) */}
      {ajukanJual && (
        <div className="fixed inset-0 z-[65] bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setAjukanJual(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="rt-ajukan-jual-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <RotateCcw className="w-5 h-5" /> Ajukan Retur Penjualan
                {ajukanJual.sale && <span className="font-mono text-xs text-slate-300 ml-2">{labelNota(ajukanJual.sale)}</span>}
              </h3>
              <button onClick={() => setAjukanJual(null)} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {ajukanJual.step === "cari" ? (
              <div className="p-6 space-y-4">
                <p className="text-sm text-slate-600">Cari nota lewat nama pelanggan, no HP, IMEI, atau nama barang — atau pilih tanggal untuk melihat semua nota hari itu.</p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={ajukanJual.q} onChange={e => setAjukanJual(m => ({ ...m, q: e.target.value }))}
                      onKeyDown={e => { if (e.key === "Enter") cariNota(ajukanJual.q, ajukanJual.tanggal); }}
                      placeholder="Contoh: Budi, 0812..., 3567... (IMEI), Casing" autoFocus data-testid="rt-jual-cari"
                      className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  {/* R1: memilih tanggal langsung mencari — owner memintanya
                      "setelah pilih tanggal, munculkan semua nota pada tanggal
                      tersebut", jadi tidak perlu menekan Cari lagi. */}
                  <input type="date" value={ajukanJual.tanggal || ""} data-testid="rt-jual-tanggal"
                    onChange={e => { const v = e.target.value; setAjukanJual(m => ({ ...m, tanggal: v })); cariNota(ajukanJual.q, v); }}
                    title="Tampilkan semua nota pada tanggal ini"
                    className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  {ajukanJual.tanggal && (
                    <button onClick={() => { setAjukanJual(m => ({ ...m, tanggal: "" })); cariNota(ajukanJual.q, ""); }}
                      data-testid="rt-jual-tanggal-hapus" title="Hapus filter tanggal"
                      className="h-10 px-3 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm">✕</button>
                  )}
                  <button onClick={() => cariNota(ajukanJual.q, ajukanJual.tanggal)} disabled={ajukanJual.searching} data-testid="rt-jual-cari-btn"
                    className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-50">
                    {ajukanJual.searching ? "Mencari..." : "Cari"}
                  </button>
                </div>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg max-h-[50vh] overflow-y-auto">
                  {ajukanJual.results.length === 0 && (
                    <p className="p-6 text-sm text-slate-400 text-center">{ajukanJual.searching ? "Mencari..." : "Belum ada hasil — ketik kata kunci lalu tekan Cari."}</p>
                  )}
                  {ajukanJual.results.map(s => (
                    <div key={s.id} className="p-3 flex items-center justify-between gap-3 hover:bg-slate-50" data-testid={`rt-jual-hasil-${s.id}`}>
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-slate-500">{labelNota(s)} · {formatTanggal(s.tanggal)}{s.branch ? ` · ${s.branch}` : ""}</p>
                        <p className="text-sm font-medium text-slate-900 truncate">{s.nama_pelanggan || "-"}{s.no_hp ? ` · ${s.no_hp}` : ""}</p>
                        <p className="text-xs text-slate-500 truncate">{(s.items || []).map(it => `${it.nama_barang} x${it.qty || 1}`).join(", ")}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-slate-900">{idr(s.total_harga || 0)}</p>
                        <button onClick={() => pilihNota(s)} disabled={ajukanJual.searching} data-testid={`rt-jual-pilih-${s.id}`}
                          className="mt-1 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50">Pilih</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <p className="text-slate-600">
                    Pelanggan <b className="text-slate-900">{ajukanJual.sale.nama_pelanggan || "-"}</b> · total nota {idr(ajukanJual.sale.total_harga || 0)}
                    {ajukanJual.sisaNilaiMaks > 0 && <span className="text-slate-500"> · sisa hak retur {idr(ajukanJual.sisaNilaiMaks)}</span>}
                  </p>
                  <button onClick={() => setAjukanJual(m => ({ ...m, step: "cari", sale: null, rows: [] }))}
                    className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2">Ganti nota</button>
                </div>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {ajukanJual.rows.map((r, i) => (
                    <div key={i} className={`p-3 ${r.sisa <= 0 ? "opacity-50" : ""}`} data-testid={`rt-jual-baris-${i}`}>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={r.checked} disabled={r.sisa <= 0} data-testid={`rt-jual-item-${i}`}
                          onChange={e => ubahBarisJual(i, { checked: e.target.checked, qtyRetur: e.target.checked ? (r.qtyRetur || Math.min(1, r.sisa)) : r.qtyRetur })}
                          className="w-4 h-4 mt-0.5 rounded border-slate-300" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{r.nama_barang}</p>
                          <p className="text-xs text-slate-500">{idr(r.harga)} · dibeli {r.qtyAsal}{r.sudah > 0 ? `, sudah diretur ${r.sudah}` : ""}{r.sisa <= 0 ? " — habis diretur" : ""}</p>
                          {r.komponen && r.komponen.length > 0 && (
                            <p className="text-[11px] text-amber-700 mt-0.5">Paket — isinya: {r.komponen.map(k => `${k.sku} x${k.qty}`).join(", ")}</p>
                          )}
                        </div>
                        {r.checked && (
                          <input type="number" min={1} max={r.sisa} value={r.qtyRetur} data-testid={`rt-jual-qty-${i}`}
                            onChange={e => ubahBarisJual(i, { qtyRetur: Math.max(0, Math.min(r.sisa, parseInt(e.target.value, 10) || 0)) })}
                            onClick={e => e.stopPropagation()}
                            className="w-16 h-8 px-2 rounded border border-slate-200 text-sm text-center" />
                        )}
                      </label>
                      {r.checked && (
                        <input value={r.alasan} onChange={e => ubahBarisJual(i, { alasan: e.target.value })} data-testid={`rt-jual-alasan-${i}`}
                          placeholder="Alasan retur barang ini (wajib)"
                          className="mt-2 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-slate-600 mb-1">Bentuk pengembalian</p>
                    {/* Tukar unit hanya untuk nota ber-nama & ber-No HP sah (I1,
                        22 Sep 2026) — aturan satu sumber lib/retur.js, cermin
                        penjaga server. Alasan ditulis, bukan tooltip. */}
                    {(() => {
                      const syarat = syaratTukarUnit(ajukanJual.sale);
                      // no. 33: tagihan bertempo belum lunas mematikan KEDUA
                      // pilihan, bukan hanya tukar unit.
                      const tagihan = syaratTagihanRetur(ajukanJual.sale);
                      return (
                        <>
                          <div className="flex gap-2">
                            {[["uang", "Uang kembali"], ["tukar_unit", "Tukar unit (kredit pelanggan)"]].map(([v, l]) => (
                              <button key={v} onClick={() => setAjukanJual(m => ({ ...m, jenisKembali: v }))} data-testid={`rt-jual-jenis-${v}`}
                                disabled={tagihan.ditahan || (v === "tukar_unit" && !syarat.boleh)}
                                className={`h-9 px-3 rounded-lg text-xs font-medium border disabled:opacity-40 disabled:cursor-not-allowed ${ajukanJual.jenisKembali === v ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700"}`}>{l}</button>
                            ))}
                          </div>
                          {syarat.boleh && ajukanJual.jenisKembali === "tukar_unit" && (
                            <p className="text-[11px] text-violet-700 mt-1" data-testid="rt-jual-tukar-hint">
                              Kredit hanya bisa dipakai pelanggan atas nama <b>{syarat.nama}</b> · No HP <b>{syarat.noHp}</b>.
                            </p>
                          )}
                          {tagihan.ditahan ? (
                            <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mt-1"
                               data-testid="rt-jual-tagihan-ditahan">
                              {tagihan.alasan} Pengembalian uang maupun tukar unit sama-sama belum
                              bisa diajukan untuk nota ini.
                            </p>
                          ) : !syarat.boleh && (
                            <p className="text-[11px] text-amber-800 mt-1" data-testid="rt-jual-tukar-terkunci">
                              Tukar unit tidak tersedia: {syarat.alasan}
                            </p>
                          )}
                          {syarat.perluHp && (
                            <GerbangLengkapiNoHp saleId={ajukanJual.sale.id} prefix="rt-jual"
                              onSelesai={hp => setAjukanJual(m => (m && m.sale
                                ? { ...m, sale: { ...m.sale, no_hp: hp } } : m))} />
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {ajukanJual.jenisKembali !== "tukar_unit" && (
                    <div>
                      <p className="text-xs font-medium text-slate-600 mb-1">Akun kas/bank pengembalian</p>
                      <select value={ajukanJual.akun} onChange={e => setAjukanJual(m => ({ ...m, akun: e.target.value }))} data-testid="rt-jual-akun"
                        className="w-full h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
                        <option value="">Pilih akun...</option>
                        {kasBank.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                      </select>
                      {kasBankError && <p className="text-[11px] text-rose-600 mt-1">{kasBankError}</p>}
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-slate-600 mb-1">Nominal kembali {ajukanJual.nominalTouched ? "" : "(saran otomatis)"}</p>
                    <InputRupiah value={ajukanJual.nominal} onChange={v => setAjukanJual(m => ({ ...m, nominal: Number(v) || 0, nominalTouched: true }))}
                      testid="rt-jual-nominal" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-600 mb-1">Catatan</p>
                    <input value={ajukanJual.catatan} onChange={e => setAjukanJual(m => ({ ...m, catatan: e.target.value }))}
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setAjukanJual(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                  <button onClick={submitAjukanJual} disabled={ajukanJual.submitting} data-testid="rt-jual-submit"
                    className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" /> {ajukanJual.submitting ? "Mengirim..." : "Ajukan Retur"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AJUKAN RETUR PEMBELIAN */}
      {ajukanBeli && (
        <div className="fixed inset-0 z-[65] bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setAjukanBeli(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="rt-ajukan-beli-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Truck className="w-5 h-5" /> Ajukan Retur Pembelian
              </h3>
              <button onClick={() => setAjukanBeli(null)} className="p-2 hover:bg-white/20 rounded-lg" data-testid="rt-ajukan-beli-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {ajukanBeli.step === "cari" ? (
              <div className="p-6 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Cari PO (hanya yang sudah menerima barang)</p>
                <p className="text-xs text-slate-500">Cari lewat kode PO, supplier, nama barang, atau IMEI — atau pilih tanggal untuk melihat semua PO hari itu.</p>
                <div className="flex gap-2">
                  <input value={ajukanBeli.poQuery}
                    onChange={e => setAjukanBeli(m => ({ ...m, poQuery: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") cariPo(ajukanBeli.poQuery, ajukanBeli.poTanggal); }}
                    placeholder="Kode PO / nama supplier..." data-testid="rt-ajukan-beli-cari-input"
                    className={inp} />
                  {/* R2: sama dengan retur penjualan — memilih tanggal langsung
                      mencari, tidak perlu menekan Cari lagi. */}
                  <input type="date" value={ajukanBeli.poTanggal || ""} data-testid="rt-beli-tanggal"
                    onChange={e => { const v = e.target.value; setAjukanBeli(m => ({ ...m, poTanggal: v })); cariPo(ajukanBeli.poQuery, v); }}
                    title="Tampilkan semua PO pada tanggal ini"
                    className="h-10 px-3 rounded-lg border border-slate-200 text-sm shrink-0 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  {ajukanBeli.poTanggal && (
                    <button onClick={() => { setAjukanBeli(m => ({ ...m, poTanggal: "" })); cariPo(ajukanBeli.poQuery, ""); }}
                      data-testid="rt-beli-tanggal-hapus" title="Hapus filter tanggal"
                      className="h-10 px-3 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm shrink-0">✕</button>
                  )}
                  <button onClick={() => cariPo(ajukanBeli.poQuery, ajukanBeli.poTanggal)} data-testid="rt-ajukan-beli-cari-btn"
                    className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-1.5 shrink-0">
                    <Search className="w-4 h-4" /> Cari
                  </button>
                </div>
                {ajukanBeli.searching ? (
                  <p className="text-xs text-slate-500 py-4 text-center">Mencari...</p>
                ) : (
                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
                    {ajukanBeli.poResults.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-8">
                        {ajukanBeli.poQuery
                          ? "Tidak ada PO ditemukan (atau belum ada barang diterima)."
                          : "Ketik kode PO, nama supplier, NAMA BARANG, atau IMEI lalu tekan Cari."}
                      </p>
                    ) : ajukanBeli.poResults.map(po => (
                      <button key={po.id} type="button" onClick={() => pilihPo(po)}
                        data-testid={`rt-ajukan-beli-po-${po.id}`}
                        className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-sm">
                        <p className="font-medium text-slate-900">{po.code} <span className="text-slate-400 font-normal">· {po.supplier_nama}</span></p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {po.tanggal}
                          {po.qty_total ? ` · diterima ${po.qty_diterima}/${po.qty_total} unit` : ""}
                          {" · status "}{po.status}
                        </p>
                        {/* Kenapa PO ini muncul — penting saat dicari lewat IMEI:
                            tanpa ini, staf melihat daftar PO tanpa tahu yang mana
                            yang memuat unit yang mereka pegang. */}
                        {po.cocok_karena && (
                          <p className="text-[11px] text-emerald-700 mt-0.5">cocok: {po.cocok_karena}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {!ajukanBeli.po ? (
                  <LoadingState loading jumlahKartu={1} data-testid="rt-ajukan-beli-loading" />
                ) : (
                  <>
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{ajukanBeli.po.code} · {ajukanBeli.po.supplier_nama}</p>
                        <p className="text-xs text-slate-500">{ajukanBeli.po.tanggal} · {ajukanBeli.po.warehouse_nama}</p>
                      </div>
                      <button onClick={() => setAjukanBeli(m => ({ ...m, step: "cari", po: null, items: [] }))}
                        data-testid="rt-ajukan-beli-ganti-po"
                        className="text-xs font-medium text-emerald-700 hover:text-emerald-800">Ganti PO</button>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Pilih Barang</p>
                      {ajukanBeli.items.map((r, i) => (
                        <div key={r.sku}
                          className={`border rounded-lg p-3 ${r.sisa <= 0 ? "border-slate-100 bg-slate-50 opacity-60" : "border-slate-200"}`}>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{r.nama}</p>
                              <p className="text-[11px] font-mono text-slate-400">{r.sku}</p>
                            </div>
                            <span className="text-[11px] text-slate-500 shrink-0 text-right">
                              Diterima {r.diterima} · sisa {r.sisa}
                            </span>
                          </div>
                          {r.sisa <= 0 ? (
                            <p className="text-[11px] text-slate-400">Belum diterima / sudah diretur semua.</p>
                          ) : r.hasImei ? (
                            r.unitCandidates.length === 0 ? (
                              <p className="text-[11px] text-amber-600">Tidak ada IMEI/SN tersedia (kemungkinan sudah diretur semua).</p>
                            ) : (
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {r.unitCandidates.map(u => {
                                  const idf = u.identifier;
                                  const cek = r.checkedImeis.includes(idf);
                                  const bisa = bisaDiretur(u.status);
                                  return (
                                    <button type="button" key={idf} disabled={!bisa}
                                      onClick={() => toggleImeiBeli(i, idf)}
                                      title={bisa ? "" : `Status unit '${u.status}' — hanya unit tersedia/rusak yang bisa dikembalikan ke supplier`}
                                      data-testid={`rt-ajukan-beli-imei-${idf}`}
                                      className={`px-2 py-1 rounded border text-[11px] font-mono inline-flex items-center gap-1 ${
                                        !bisa ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
                                          : cek ? "bg-rose-100 border-rose-300 text-rose-800"
                                            : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                                      {idf}
                                      {u.status && u.status !== "tersedia" && (
                                        <span className={`px-1 rounded text-[10px] font-sans ${
                                          u.status === "rusak" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>
                                          {u.status}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )
                          ) : (
                            <label className="text-[11px] text-slate-500 inline-flex items-center gap-2 mt-1">
                              Qty
                              <input type="number" min={0} max={r.sisa} value={r.qty || ""}
                                onChange={e => setQtyBeli(i, e.target.value)}
                                data-testid={`rt-ajukan-beli-qty-${r.sku}`}
                                className="h-8 w-24 px-2 rounded border border-slate-200 text-sm" />
                            </label>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field l="Kompensasi">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-h-10">
                          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                            <input type="radio" name="rt-kompensasi" checked={ajukanBeli.kompensasi === ""}
                              onChange={() => setAjukanBeli(m => ({ ...m, kompensasi: "", akun_terima: "" }))}
                              data-testid="rt-ajukan-beli-kompensasi-nanti" />
                            Tentukan nanti
                          </label>
                          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                            <input type="radio" name="rt-kompensasi" checked={ajukanBeli.kompensasi === "potong_utang"}
                              onChange={() => setAjukanBeli(m => ({ ...m, kompensasi: "potong_utang" }))}
                              data-testid="rt-ajukan-beli-kompensasi-utang" />
                            Potong Utang
                          </label>
                          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                            <input type="radio" name="rt-kompensasi" checked={ajukanBeli.kompensasi === "uang_kembali"}
                              onChange={() => setAjukanBeli(m => ({ ...m, kompensasi: "uang_kembali" }))}
                              data-testid="rt-ajukan-beli-kompensasi-uang" />
                            Uang Kembali
                          </label>
                        </div>
                        {ajukanBeli.kompensasi === "" && (
                          <p className="text-[11px] text-slate-500">
                            Nilai retur akan parkir di <b>Piutang Retur Pembelian</b> sampai bentuknya
                            dipilih setelah retur disetujui. Kredit DOA hanya bisa dipilih lewat jalur ini.
                          </p>
                        )}
                      </Field>
                      {ajukanBeli.kompensasi === "potong_utang" && (ajukanBeli.utangAktif || []).length > 1 && (
                        <Field l="Tagihan yang dipotong *">
                          <select value={ajukanBeli.utang_id || ""}
                            onChange={e => setAjukanBeli(m => ({ ...m, utang_id: e.target.value }))}
                            data-testid="rt-ajukan-beli-utang" className={inp + " bg-white"}>
                            <option value="">— pilih tagihan —</option>
                            {ajukanBeli.utangAktif.map(k => (
                              <option key={k.id} value={k.id}>
                                {k.code} · No. DO/Invoice {k.no_invoice || "-"} · sisa {idr(k.sisa)}
                              </option>
                            ))}
                          </select>
                          <p className="text-[11px] text-slate-500">
                            PO ini diterima bertahap — tiap DO punya tagihannya sendiri.
                          </p>
                        </Field>
                      )}
                      {ajukanBeli.kompensasi === "uang_kembali" && (
                        <Field l="Akun Penerima Uang">
                          <select value={ajukanBeli.akun_terima} disabled={!!kasBankError}
                            onChange={e => setAjukanBeli(m => ({ ...m, akun_terima: e.target.value }))}
                            data-testid="rt-ajukan-beli-akun" className={inp + " bg-white"}>
                            <option value="">— pilih akun kas/bank —</option>
                            {kasBank.map(a => <option key={a.id} value={a.id}>{a.name} ({idr(a.balance)})</option>)}
                          </select>
                          {kasBankError && (
                            <p className="text-[11px] text-amber-600" data-testid="rt-ajukan-beli-kasbank-error">
                              {kasBankError} Gunakan &quot;Potong Utang&quot; sebagai alternatif.
                            </p>
                          )}
                        </Field>
                      )}
                    </div>
                    <Field l="Alasan Retur *">
                      <input value={ajukanBeli.alasan}
                        onChange={e => setAjukanBeli(m => ({ ...m, alasan: e.target.value }))}
                        placeholder="mis. barang cacat / tidak sesuai pesanan"
                        data-testid="rt-ajukan-beli-alasan" className={inp} />
                    </Field>
                  </>
                )}
              </div>
            )}

            <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setAjukanBeli(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              {ajukanBeli.step === "form" && ajukanBeli.po && (
                <button onClick={submitAjukanBeli} disabled={ajukanBeli.submitting}
                  data-testid="rt-ajukan-beli-submit"
                  className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                  <Truck className="w-4 h-4" /> {ajukanBeli.submitting ? "Mengirim..." : "Ajukan Retur"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
